import { useMemo } from 'react';
import type { TocRow } from '../../lib/policy/toc.ts';

/**
 * CR-063: 규정 상세 목차를 장(章) 단위 블록의 CSS 다단으로 배치한다.
 * 단 폭 16rem: 본문 폭(61rem)에서 3단, 넓은 창에서 4단. 조 행은 번호·제목만(항 수 표시 없음).
 * 조 목록은 단 경계를 넘어 다음 단으로 이어지고(장 제목만 단 끝에 홀로 남지 않게), 단 사이에 세로선을 둔다. 장 없이 조만 있는 문서는 조 전체가 한 블록, 부칙·별표·별지는 마지막 블록.
 */
interface Group { key: string; heading: string | null; rows: TocRow[] }

export function TocColumns({ rows, onJump }: { rows: TocRow[]; onJump: (r: TocRow) => void }) {
  const groups = useMemo<Group[]>(() => {
    const out: Group[] = [];
    let cur: Group | null = null;
    const tail: TocRow[] = [];
    for (const r of rows) {
      if (r.kind === 'chapter') { cur = { key: r.key, heading: `${r.no} ${r.title}`.trim(), rows: [] }; out.push(cur); }
      else if (r.kind === 'article') { if (!cur) { cur = { key: 'root', heading: null, rows: [] }; out.push(cur); } cur.rows.push(r); }
      else tail.push(r);
    }
    if (tail.length) out.push({ key: 'tail', heading: '부칙 · 별표 · 별지', rows: tail });
    return out;
  }, [rows]);
  const chapters = rows.filter((r) => r.kind === 'chapter').length;
  const articles = rows.filter((r) => r.kind === 'article').length;
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">{chapters > 0 && <>장 {chapters} · </>}조 {articles}</div>
      <div className="border border-slate-200 px-3 py-2 text-[13px] leading-5" style={{ columnWidth: '16rem', columnGap: '1.5rem', columnRule: '1px solid #e2e8f0' }}>
        {groups.map((g) => (
          <div key={g.key} className="mb-2">
            {g.heading && <div className="font-semibold" style={{ breakAfter: 'avoid' }}>{g.heading}</div>}
            {g.rows.map((r) => (
              <button key={r.key} type="button" className="flex w-full items-baseline gap-1 text-left hover:underline" onClick={() => onJump(r)}>
                <span className="shrink-0 text-slate-500 tabular-nums">{r.no}</span>
                <span className="min-w-0 truncate">{r.title}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
